// The only public page, and the only way in.
//
// It is reached at `/` — the URL Google's OAuth brand verification reviews, so
// it must render without an account and say what the app is — and at
// `/sign-in`, which the backend redirects to when a sign-in attempt fails. Both
// paths render this, so a visitor never meets a screen whose only content is a
// button leading to the same two providers on the next page.
//
// It therefore carries everything a signed-out visitor needs: what the app is,
// the provider buttons, the language and appearance choices, and the links to
// About, the privacy policy and the terms. `/about` holds the full description;
// this page keeps one sentence and points there.
//
// On a phone browser tab the installed app is required, so that screen replaces
// the page: it shows how to install, follows the system theme, and offers a
// language choice only when the phone's language is not one the app speaks. The
// About and policy links travel with it, because a visitor deciding whether to
// install still has to be able to read them.

import { type ReactNode } from 'react';
import {
  Alert,
  Anchor,
  Button,
  Container,
  CopyButton,
  Divider,
  Group,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import {
  IconBrandApple,
  IconBrandGoogle,
  IconCheck,
  IconCopy,
  IconHourglass,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate } from '@tanstack/react-router';
import { useTranslation } from '@/i18n/I18nProvider';
import { useSession } from '@/session/SessionProvider';
import { listProviders, providerStartUrl } from '@/api/endpoints';
import { ApiError } from '@/api/errors';
import { AppearanceControl, LanguageControl } from '@/components/controls';
import { InstallScreen, useInstallRequired } from '@/components/InstallPrompt';
import { Logo } from '@/components/Logo';
import { systemLanguage } from '@/lib/preferences';
import { ErrorState, LoadingState, SketchCard } from '@/components/primitives';
import { ACCEPT_PATH, inviteLink, stashedInvite } from '@/features/groups/invites';

export default function SignInRoute(): ReactNode {
  const t = useTranslation();
  const { state } = useSession();
  const installRequired = useInstallRequired();
  // The backend redirects back here with a query flag when login fails or is
  // cancelled. Reading it from the URL keeps this screen outside the typed
  // route tree it deliberately is not part of for search parameters.
  const errorCode = new URLSearchParams(window.location.search).get('error') ?? undefined;
  const providersQuery = useQuery({
    queryKey: ['providers'],
    queryFn: ({ signal }) => listProviders({ signal }),
    staleTime: 5 * 60_000,
  });

  // Signed in already: this page has nothing left to offer, so send them to
  // their data. `offline` stays here, because without the server the app
  // cannot show anything and this page at least explains what the app is.
  if (state.status === 'authenticated') return <Navigate to="/feeds" replace />;

  if (installRequired) {
    return (
      <InstallScreen
        extra={
          <>
            <InviteCarryOver />
            {systemLanguage() === null ? (
              <Group justify="center">
                <LanguageControl />
              </Group>
            ) : null}
            {/* A phone visitor deciding whether to install still has to be able
                to read what the app does, and what it does with their data.
                This screen replaces the whole page, so the links live here. */}
            <Group justify="center" gap="md">
              <Anchor component={Link} to="/about" size="xs">
                {t('home.aboutLink')}
              </Anchor>
              <Anchor component={Link} to="/privacy" size="xs">
                {t('legal.privacyLink')}
              </Anchor>
              <Anchor component={Link} to="/terms" size="xs">
                {t('legal.termsLink')}
              </Anchor>
            </Group>
          </>
        }
      />
    );
  }

  return (
    <Container component="main" size="xs" py="xl">
      <Stack gap="lg" align="center">
        <Stack gap="sm" align="center" maw={440}>
          <Logo size={64} label={t('app.name')} />
          <Title order={1} className="sensing-hand" ta="center">
            <span className="sensing-underline">{t('app.name')}</span>
          </Title>
          <Text size="lg" ta="center">
            {t('home.lede')}
          </Text>
        </Stack>

        {/* The reason a previous attempt failed, beside the button it applies
            to rather than on a screen of its own. */}
        {errorCode === 'not_approved' ? (
          <Alert
            color="amber"
            variant="light"
            icon={<IconHourglass size={18} aria-hidden />}
            title={t('auth.awaitingApprovalTitle')}
            w="100%"
          >
            <Text size="sm">{t('auth.awaitingApproval')}</Text>
          </Alert>
        ) : errorCode ? (
          <ErrorState
            error={
              new ApiError({ code: errorCode === 'cancelled' ? 'aborted' : 'unauthenticated' })
            }
            title={t('auth.error')}
          />
        ) : null}

        {/* The way in, in the card this page always used. */}
        <SketchCard>
          <Stack gap="md" align="stretch">
            {providersQuery.isPending ? <LoadingState /> : null}

            {providersQuery.isError ? (
              <ErrorState
                error={providersQuery.error}
                onRetry={() => void providersQuery.refetch()}
              />
            ) : null}

            {providersQuery.isSuccess ? <ProviderButtons providers={providersQuery.data} /> : null}

            <Text size="xs" c="dimmed" ta="center">
              {t('home.accountHint')}
            </Text>
          </Stack>
        </SketchCard>

        {/* Reachable before login: the choice made here is the one the
            signed-in app uses, so it belongs next to the button it applies
            to. */}
        <Group justify="center" gap="xs">
          <LanguageControl />
          <AppearanceControl />
        </Group>

        <Divider color="var(--sensing-border)" w="100%" />

        {/* The consent screen names a privacy policy, and Google requires the
            app's pages to describe its functionality: the description and the
            policy are one link from here, below the way in rather than
            competing with it. */}
        <Group justify="center" gap="md">
          <Anchor component={Link} to="/about" size="xs">
            {t('home.aboutLink')}
          </Anchor>
          <Anchor component={Link} to="/privacy" size="xs">
            {t('legal.privacyLink')}
          </Anchor>
          <Anchor component={Link} to="/terms" size="xs">
            {t('legal.termsLink')}
          </Anchor>
        </Group>
      </Stack>
    </Container>
  );
}

function ProviderButtons({
  providers,
}: {
  providers: { google: boolean; apple: boolean };
}): ReactNode {
  const t = useTranslation();
  // An invite opened before signing in is accepted right after it; everywhere
  // else the visitor wants their data. The page they are on is not a useful
  // return path: it is this one.
  const returnTo = stashedInvite() ? ACCEPT_PATH : '/feeds';

  if (!providers.google && !providers.apple) {
    return (
      <Text size="sm" c="dimmed" ta="center">
        {t('auth.noProviders')}
      </Text>
    );
  }

  return (
    <Stack gap="sm">
      {providers.google ? (
        <Button
          component="a"
          href={providerStartUrl('google', returnTo)}
          leftSection={<IconBrandGoogle size={18} aria-hidden />}
          variant="filled"
          size="md"
          fullWidth
        >
          {t('auth.continueWithProvider', { provider: 'Google' })}
        </Button>
      ) : null}
      {providers.apple ? (
        <Button
          component="a"
          href={providerStartUrl('apple', returnTo)}
          leftSection={<IconBrandApple size={18} aria-hidden />}
          variant="default"
          size="md"
          fullWidth
        >
          {t('auth.continueWithProvider', { provider: 'Apple' })}
        </Button>
      ) : null}
    </Stack>
  );
}

/**
 * An invite opened in a phone's browser, which must install the app first.
 * The installed app does not share this tab's storage, so the link is offered
 * for copying and pasting into the app.
 */
function InviteCarryOver(): ReactNode {
  const t = useTranslation();
  const token = stashedInvite();
  if (!token) return null;
  return (
    <SketchCard>
      <Stack gap="xs">
        <Text size="sm">{t('invite.installFirst')}</Text>
        <CopyButton value={inviteLink(token)}>
          {({ copied, copy }) => (
            <Button
              variant="light"
              leftSection={
                copied ? <IconCheck size={16} aria-hidden /> : <IconCopy size={16} aria-hidden />
              }
              onClick={copy}
            >
              {copied ? t('sharing.copied') : t('invite.copyLink')}
            </Button>
          )}
        </CopyButton>
      </Stack>
    </SketchCard>
  );
}
